import React, { useState } from "react";
import { API_ROOT } from './api_root';
import { Button, Input, Modal, Space, Typography, Upload, message } from "antd";
import logo from "./assets/images/logo.png";
import { CloseOutlined, UploadOutlined } from '@ant-design/icons';


import "antd/dist/reset.css";
import "./styles/App.css";
import "./styles/fonts.css";
import "./styles/global-antd-overrides.scss";

import { ConfigProvider } from "antd";

import theme from "./styles/theme";
import deDE from "antd/locale/de_DE";

const { Text, Title } = Typography;

interface CompanyWarningModalProps {
  open: boolean;
  setIsOpen: (open: boolean) => void;
  companies: string[];
  onConfirm?: () => void;
}

const CompanyWarningModal: React.FC<CompanyWarningModalProps> = ({
  open,
  setIsOpen,
  companies,
  onConfirm,
}) => (
  <Modal
    width={780}
    open={open}
    onCancel={() => setIsOpen(false)}
    footer={
      <Space direction="horizontal" style={{ width: "100%", marginTop: 16, justifyContent: "space-between", alignItems: "start" }}>
        <div style={{ textAlign: 'start' }} ><Text style={{ textAlign: 'start' }} type="danger">
          Bitte bestätige, dass dies korrekt ist oder breche den Vorgang ab und wähle andere Dateien aus.</Text></div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button type="default" onClick={() => setIsOpen(false)}>
            Abbrechen & Zurück
          </Button>
          <Button
            type="primary"
            style={{
              background: "#222",
              color: "#fff",
              border: "none",
              width: 180,
            }}
            onClick={() => {
              setIsOpen(false);
              onConfirm && onConfirm();
            }}
          >
            Bestätigen
          </Button>
        </div>
      </Space>
    }
   
    title={<Title level={5} type="danger" >Achtung, du versuchst Daten von verschiedenen Firmen zu kombinieren:</Title>}
  >
    <ul>
      {companies.map((company, idx) => (
        <li key={idx} style={{ display: "block" }}>- {company}</li>
      ))}
    </ul>
  </Modal>
);

function App() {
  const [token, setToken] = useState<string | null>(null);
  const [apiKey, setApiKey] = useState("");
  const [fileList, setFileList] = useState<any[]>([]); // Für die gesammelten Dateien
  const [uploading, setUploading] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfFilename, setPdfFilename] = useState<string>("Kombinierter Bericht.pdf");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [displayName, setDisplayName] = useState<string>("");

  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [companyList, setCompanyList] = useState<string[]>([]);

  const [displayNameDisabled, setDisplayNameDisabled] = useState(false);


  const handleUpload = async () => {
    if (!fileList.length) return;
    setUploading(true);
    const formData = new FormData();
    fileList.forEach(file => {
      formData.append("files", file);
    });
    if (logoFile) {
      formData.append("logo", logoFile);
    }
    if (displayName) {
    formData.append("displayName", displayName); // <--- Anzeigename mitsenden
  }

    try {
      const response = await fetch(`${API_ROOT}/report`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      if (response.ok) {
        const blob = await response.blob();
        const disposition = response.headers.get("Content-Disposition");
        const companies = response.headers.get("X-Companies");
        if (companies) {
          const companiesArray = companies.split(",,");
          if (companiesArray.length > 1) {
            setCompanyList(companiesArray);
            setCompanyModalOpen(true);
          }
        }
        let filename = "Kombinierter Bericht.pdf";
        if (disposition) {
          const match = disposition.match(/filename="?([^"]+)"?/);
          if (match) filename = match[1];
        }
        setPdfFilename(filename);
        const pdfUrl = URL.createObjectURL(blob);
        setPdfBlobUrl(pdfUrl);
        message.success("PDF erfolgreich generiert");
      } else {
        if (response.status === 401) {
          message.error("Authentifizierung fehlgeschlagen.")
          setToken(null); // Setze Token zurück, um erneute Authentifizierung zu erzwingen
        } else if (response.status === 400) {
          // const errorData = await response.json();
          // message.error(errorData.detail || "Unbekannter Fehler");
          const errorData = await response.json();
          if (
            errorData.detail &&
            errorData.detail.startsWith("Die CSV-Dateien enthalten unterschiedliche Firmen")
          ) {
            Modal.warning({
              title: "Achtung, du versuchst Daten von verschiedenen Firmen zu kombinieren:",
              content: errorData.detail,
              okText: "OK",
            });
          } else {
            message.error(errorData.detail || "Unbekannter Fehler");
          }
        } else {
          message.error("Fehler beim Generieren der PDF");
        }
      }
    } catch (err) {
      message.error("Fehler beim Generieren der PDF");
    }
    setUploading(false);
  };


  const handleAuth = async () => {
    message.loading({ content: "Authentifizierung...", key: "auth" });
    try {
      const response = await fetch(`${API_ROOT}/token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ api_key: apiKey }),
      });
      if (response.status !== 200) {
        message.error({ content: "Authentifizierung fehlgeschlagen", key: "auth" });
        console.error("Authentication failed");
        return;
      }
      const data = await response.json();
      const { access_token } = data;
      message.success({ content: "Authentifiziert", key: "auth" });
      setToken(access_token);
    } catch (error) {
      message.error({ content: "Authentifizierung fehlgeschlagen", key: "auth" });
    }
  };



  const uploadProps = {
    multiple: true,
    accept: ".csv",
    fileList,
    beforeUpload: (file: File) => {
      setFileList(prev => [...prev, file]);
      return false; // Verhindert automatisches Hochladen
    },
    onRemove: (file: any) => {
      setFileList(prev => prev.filter(f => f.uid !== file.uid));
    },
    showUploadList: true,
  };



  return (
    <>
      <ConfigProvider theme={theme} locale={deDE}>
        <Space className="Header" style={{ display: 'flex', justifyContent: 'space-between' }} >
          <div style={{ paddingLeft: "5vw" }}>
            <img src={logo} alt="logo" style={{ width: 100 }} />
          </div>
        </Space >
        <div className="content" >
          {!token ? (
            <div style={{ display: 'flex', justifyContent: 'center', textAlign: 'center' }}>
              <div style={{ padding: 16, marginTop: 50 }}>
                <Input
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Enter Password"
                  onPressEnter={handleAuth}
                />
                <Button style={{ marginTop: 16 }} onClick={handleAuth}>Authentifizieren</Button>
              </div>
            </div>
          ) : (
            <div>

              <div style={{ width: "100%", display: 'flex', flexDirection: 'row', alignItems: 'start' }}>
                <div style={{
                  marginTop: 24,
                  // minWidth: 300, 
                  width: 530,
                  marginRight: 24
                }}>
                  <h3>FIT Day Reports (PDF) erstellen</h3>
                  <div style={{ maxWidth: 520, fontSize: 14, marginBottom: 16 }}>
                  <Text >
                    Dieses Tool ermöglicht dir den bekannten FIT Day Report (PDF) zu erstellen und v.A. mehrere FIT Days in einem Report zu kombinieren (wenn z.B. mehrere Laptops genutzt oder an mehreren Tagen vermessen wurde).
                  </Text>
                  </div>
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <Space direction="horizontal" style={{ width: "100%" }} align="start">
                      <div style={{ width: 180 }}>
                        <Text>Datei auswählen*</Text>
                      </div>
                      <div style={{ width: 320, height: 'auto' }}>
                        <Upload {...uploadProps} disabled={!token}>
                          <Button icon={<UploadOutlined />} disabled={!token || uploading}
                            style={{ whiteSpace: "normal", width: 320, height: 'auto' }} >
                            Datei(en) auswählen (.csv)
                          </Button>
                        </Upload>
                      </div>
                    </Space>
                    <Space direction="horizontal" style={{ width: "100%" }} align="start">
                      <div style={{ width: 180 }}>
                        <Text>Anzeigename definieren</Text>
                      </div>
                      <div>
                        <Input
                          style={{ width: 320, height: 'auto' }}
                          value={displayName}
                          onChange={(e) => setDisplayName(e.target.value)}
                          placeholder="Anzeigename der Firma hinzufügen"
                          disabled={displayNameDisabled}
                        />
                      </div>
                    </Space>
                    <Text>oder</Text>
                    <Space direction="horizontal" style={{ width: "100%" }} align="start">
                      <div style={{ width: 180 }}>
                        <Text>Firmen-Logo hochladen</Text>
                      </div>
                      <div style={{ width: 320 }}>
                        <Upload
                          accept="image/png,image/jpeg"
                          showUploadList={false}
                          beforeUpload={file => {
                            setLogoFile(file);
                            setDisplayName("");           // Anzeigename leeren
                            setDisplayNameDisabled(true); // Anzeigename-Eingabe deaktivieren
                            return false;
                          }}
                        >
                          <Button icon={<UploadOutlined />} style={{ whiteSpace: "normal", width: 320, height: 'auto' }} >
                            Firmenlogo hochladen (png / jpg)
                          </Button>
                        </Upload>
                        {logoFile && (
                          <div style={{
                            marginTop: 8,
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            position: "relative"
                          }}>
                            <div style={{ position: "relative", display: "inline-block" }}>
                              <img
                                src={URL.createObjectURL(logoFile)}
                                alt="Logo Vorschau"
                                style={{ maxWidth: 80, maxHeight: 60, border: "1px solid #ccc", borderRadius: 4 }}
                              />
                              <Button
                                size="small"
                                shape="circle"
                                style={{
                                  position: "absolute",
                                  top: -8,
                                  right: -8,
                                  zIndex: 2,
                                  width: 22,
                                  height: 22,
                                  padding: 0,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center"
                                }}
                                onClick={() => {
                                  setLogoFile(null);
                                  setDisplayNameDisabled(false);
                                }}
                                icon={<CloseOutlined style={{ fontSize: 8 }} />}
                              />
                            </div>
                          </div>
                        )}

                      </div>
                    </Space>
                  </Space>

                  <div style={{ marginLeft: 180 }}>


                    <Button
                      type="primary"
                      onClick={handleUpload}
                      disabled={!fileList.length || !token || uploading}
                      loading={uploading}
                      style={{ marginLeft: 8, marginTop: 16, width: 320 }}
                    >
                      Reports erstellen
                    </Button>
                  </div>
                </div>
                <div style={{ width: "100%" }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', height: 40, width: '100%', marginTop: 24 }}>
                    <div><h3>Vorschau</h3></div>
                    <div>
                      {fileList.length !== 0 && <Button

                        type="default"
                        style={{ width: 180 }}
                        onClick={() => {
                          setPdfBlobUrl(null);
                          setFileList([]);
                          setLogoFile(null);
                          setDisplayName("");
                          // setToken(null); // Zurück zur Authentifizierung
                        }}
                      >
                        Abbrechen & Zurück
                      </Button>}
                    </div>
                  </div>

                  {pdfBlobUrl ? (
                    <div style={{ marginTop: 16, width: "100%" }}>
                      {/* <h3>PDF Vorschau</h3> */}
                      <iframe
                        src={pdfBlobUrl ? `${pdfBlobUrl}#toolbar=0&navpanes=0` : undefined}
                        title="PDF Vorschau"
                        width="100%"
                        height="auto"
                        style={{
                          aspectRatio: "7 / 5",
                          border: "1px solid #ccc",
                          display: "block",
                          margin: "0 auto",
                          maxWidth: "100%",
                          minWidth: 0,
                          minHeight: 0,
                          position: "relative"
                        }}
                      // style={{ border: "1px solid #ccc", display: "block", margin: "0 auto" }}
                      />
                      <Space style={{ display: 'flex', justifyContent: 'end' }}>
                        <Button
                          type="primary"
                          style={{ marginTop: 24, width: 290, marginBottom: 16 }}
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = pdfBlobUrl;
                            link.download = pdfFilename;
                            link.click();
                          }}
                        >
                          Report finalisieren und herunterladen
                        </Button>
                      </Space>
                    </div>
                  ) : (
                    <div style={{ marginTop: 16, width: "100%", textAlign: "center" }}>
                      <div style={{
                        aspectRatio: "7 / 5",
                        border: "1px solid #ccc",
                        // display: "block",
                        margin: "0 auto",
                        maxWidth: "100%",
                        minWidth: 0,
                        minHeight: 0,
                        position: "relative",
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <div style={{
                          maxWidth: 300,
                          fontSize: 12,
                        }}>
                          Die Vorschau des Reports wird erstellt, sobald du Dateien ausgewählt und auf “Report erstellen” geklickt hast.
                        </div></div>

                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        <CompanyWarningModal
          open={companyModalOpen}
          setIsOpen={setCompanyModalOpen}
          companies={companyList}
        />
      </ConfigProvider >
    </>
  );
}

export default App;
